import React, { useEffect, useRef, useState } from "react";
import Select, { components } from "react-select";
import { sortBy } from "underscore";
import { useGetUserCompanies } from "../../../../backend/query";
import { FULL_USER_COMPANIES_PAYLOAD } from "../../../../backend/license";
import { useAssociateCompanyLogo } from "../../../../contexts/AssociateCompanyContext";

interface LicenseFiltersProps {
  onChange: (filters: any) => void;
  showStateCityInsteadOfLocation?: boolean;
  showStateCity?: boolean;
}

const ALL_OPTION = {
  value: "all",
  label: "ALL",
  company: null,
  associate_company: null,
  location: null,
  state: null,
  city: null,
};

function LicenseFilters({
  onChange,
  showStateCityInsteadOfLocation = false,
  showStateCity = false,
}: LicenseFiltersProps) {
  // Company has no "All" option — a concrete selection is always required,
  // auto-selected first alphabetically (see the auto-select effect below).
  // Associate Company DOES have "All" (ALL_OPTION) — it starts there and only
  // moves to a specific AC on a manual pick; "All" shows the parent company's
  // own branding via setAssociateCompanyBranding (see AssociateCompanyContext).
  const [company, setCompany] = useState<any>(null);
  const [associateCompany, setAssociateCompany] = useState<any>(ALL_OPTION);
  const [location, setLocation] = useState<any>(ALL_OPTION);
  const [state, setState] = useState<any>(ALL_OPTION);
  const [city, setCity] = useState<any>(ALL_OPTION);

  const [companies, setCompanies] = useState<any[]>([]);
  const [associateCompanies, setAssociateCompanies] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);

  const { userCompanies, isFetching } = useGetUserCompanies(FULL_USER_COMPANIES_PAYLOAD, true);
  const { setAssociateCompanyBranding } = useAssociateCompanyLogo();

  // Guard so a re-render (e.g. userCompanies keeping the same array reference)
  // never clobbers a manual selection the user already made.
  const companyRef = useRef<any>(null);
  companyRef.current = company;

  // Company list loaded: sort alphabetically and auto-select the first one,
  // unless a company is already selected (manual pick or a prior auto-select).
  // Sorted list + default selection are computed together in this one effect
  // so there is no render gap where the list looks "loaded but empty".
  useEffect(() => {
    if (!userCompanies || userCompanies.length === 0) return;
    const sortedCompanies = sortBy(userCompanies, (c: any) => (c.name || "").toLowerCase());
    const companyOptions = sortedCompanies.map(function (c: any) {
      return { value: c.id, label: c.name, company: c };
    });
    setCompanies(companyOptions);
    if (!companyRef.current) {
      setCompany(companyOptions[0]);
    }
  }, [userCompanies]);

  // Company changed (manual pick or auto-select): rebuild the Associate Company
  // list from the nested tree. Associate Company resets to "All" here rather
  // than auto-selecting a specific AC — "All" shows the parent company's own
  // branding (company.company is the full raw record from the same tree fetch,
  // so it already carries logo/favicon/whitelabeling_enabled/parent_company_id).
  // List + default are computed together in this one effect — no separate
  // "copy data" + "pick default" steps — so there is no stale render that
  // could look like "loaded but empty" and flash the default logo.
  useEffect(() => {
    if (!company || !company.company) {
      setAssociateCompanies([]);
      if (!company) setAssociateCompany(ALL_OPTION);
      return;
    }
    const sortedAssociateCompanies = sortBy(
      company.company.associate_companies || [],
      (ac: any) => (ac.name || "").toLowerCase(),
    );
    const assocCompanies = sortedAssociateCompanies.map(function (ac: any) {
      return {
        value: ac.id,
        label: ac.name,
        associate_company: ac,
        locations: ac.locations || [],
        favicon: ac.favicon,
        whitelabeling_enabled: ac.whitelabeling_enabled,
      };
    });
    setAssociateCompanies([ALL_OPTION, ...assocCompanies]);
    setAssociateCompany(ALL_OPTION);
    setAssociateCompanyBranding(company.company);
  }, [company]);

  useEffect(() => {
    if (associateCompany && associateCompany.locations) {
      const locationOptions = [
        ALL_OPTION,
        ...associateCompany.locations.map(function (loc: any) {
          const label =
            loc.city && loc.city.name
              ? loc.name + ", " + loc.city.name
              : loc.name;
          return { value: loc.id, label: label, location: loc };
        }),
      ];
      setLocations(locationOptions);
      setLocation(ALL_OPTION);

      if (showStateCityInsteadOfLocation || showStateCity) {
        const statesMap = new Map();
        associateCompany.locations.forEach((loc: any) => {
          if (loc.state && loc.state.id) {
            statesMap.set(loc.state.id, loc.state);
          }
        });

        const stateOptions = [
          ALL_OPTION,
          ...Array.from(statesMap.values()).map((state: any) => ({
            value: state.id,
            label: state.name,
            state: state,
          })),
        ];
        setStates(stateOptions);
        setState(ALL_OPTION);
      } else {
        setStates([ALL_OPTION]);
        setState(ALL_OPTION);
      }
      setCities([ALL_OPTION]);
      setCity(ALL_OPTION);
    } else {
      setLocations([ALL_OPTION]);
      setLocation(ALL_OPTION);
      setStates([ALL_OPTION]);
      setState(ALL_OPTION);
      setCities([ALL_OPTION]);
      setCity(ALL_OPTION);
    }
  }, [associateCompany, showStateCityInsteadOfLocation, showStateCity]);

  useEffect(() => {
    if (showStateCityInsteadOfLocation || showStateCity) {
      // State/City are independent, user-driven filters in these modes;
      // don't let picking a Location collapse their option lists.
      return;
    }
    if (location && location.value === "all") {
      setStates([ALL_OPTION]);
      setState(ALL_OPTION);
      setCities([ALL_OPTION]);
      setCity(ALL_OPTION);
    } else if (location && location.location && location.location.state) {
      const stateData = location.location.state;
      const stateOption = [
        ALL_OPTION,
        { value: stateData.id, label: stateData.name, state: stateData },
      ];
      setStates(stateOption);
      setState(ALL_OPTION);
      setCities([ALL_OPTION]);
      setCity(ALL_OPTION);
    } else {
      setStates([]);
      setState(null);
      setCities([]);
      setCity(null);
    }
  }, [location, showStateCityInsteadOfLocation, showStateCity]);

  useEffect(() => {
    if (state && state.value === "all") {
      setCities([ALL_OPTION]);
      setCity(ALL_OPTION);
    } else if (state && state.state && state.state.cities) {
      const cityOptions = [
        ALL_OPTION,
        ...state.state.cities.map(function (c: any) {
          return { value: c.id, label: c.name, city: c };
        }),
      ];
      setCities(cityOptions);
      setCity(ALL_OPTION);
    } else if ((showStateCityInsteadOfLocation || showStateCity) && state && state.state && associateCompany && associateCompany.locations) {
      const citiesMap = new Map();
      associateCompany.locations.forEach((loc: any) => {
        if (loc.state && loc.state.id === state.value && loc.city && loc.city.id) {
          citiesMap.set(loc.city.id, loc.city);
        }
      });

      const cityOptions = [
        ALL_OPTION,
        ...Array.from(citiesMap.values()).map((city: any) => ({
          value: city.id,
          label: city.name,
          city: city
        }))
      ];
      setCities(cityOptions);
      setCity(ALL_OPTION);
    } else if (location && location.location && location.location.city) {
      const cityData = location.location.city;
      setCities([
        ALL_OPTION,
        { value: cityData.id, label: cityData.name, city: cityData },
      ]);
      setCity(ALL_OPTION);
    } else {
      setCities([]);
      setCity(null);
    }
  }, [state, showStateCityInsteadOfLocation, showStateCity, associateCompany]);

  // Narrow the Location options to those matching the selected State/City
  // when both Location and State/City are shown together.
  useEffect(() => {
    if (!showStateCity) return;
    if (!associateCompany || !associateCompany.locations) {
      return;
    }

    let filteredLocations = associateCompany.locations;
    if (state && state.value !== "all") {
      filteredLocations = filteredLocations.filter(
        (loc: any) => loc.state && loc.state.id === state.value
      );
    }
    if (city && city.value !== "all") {
      filteredLocations = filteredLocations.filter(
        (loc: any) => loc.city && loc.city.id === city.value
      );
    }

    const locationOptions = [
      ALL_OPTION,
      ...filteredLocations.map(function (loc: any) {
        const label =
          loc.city && loc.city.name
            ? loc.name + ", " + loc.city.name
            : loc.name;
        return { value: loc.id, label: label, location: loc };
      }),
    ];
    setLocations(locationOptions);
    // Reset Location whenever State/City narrows or widens the option set,
    // so a stale Location selection never survives a State/City change
    // (e.g. picking a Location under a State, then setting State back to "ALL").
    setLocation(ALL_OPTION);
  }, [state, city, associateCompany, showStateCity]);

  useEffect(() => {
    const filters: any = {};
    if (company && company.value) {
      filters.company_id_eq = company.value;
    }
    if (associateCompany && associateCompany.value && associateCompany.value !== "all") {
      filters.associate_company_id_eq = associateCompany.value;
    }
    if (location && location.value !== "all") {
      filters.location_id_eq = location.value;
    }
    if (state && state.value !== "all") {
      filters.state_id_eq = state.value;
    }
    if (city && city.value !== "all") {
      filters.city_id_eq = city.value;
    }
    onChange(filters);
  }, [company, associateCompany, location, state, city]);

  function handleAssociateCompanyChange(opt: any) {
    const selected = opt || ALL_OPTION;
    setAssociateCompany(selected);
    if (selected.associate_company) {
      setAssociateCompanyBranding(selected.associate_company);
    } else {
      // "All" (re-)selected — show the parent company's own branding.
      setAssociateCompanyBranding(company && company.company ? company.company : null);
    }
  }

  const DropdownIndicator = function (props: any) {
    return (
      <components.DropdownIndicator {...props}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="10"
          height="6"
          viewBox="0 0 10 6"
          fill="none"
        >
          <path
            d="M1 1L5 5L9 1"
            stroke="#1E1E1E"
            strokeWidth="1.33333"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </components.DropdownIndicator>
    );
  };

  return (
    <>
      <div className="px-2">
        <label className="filter-label">
          <small>Company</small>
        </label>
        <Select
          placeholder="Company"
          options={companies}
          onChange={setCompany}
          value={company}
          className="select-control"
          components={{ DropdownIndicator }}
          isLoading={isFetching}
        />
      </div>

      <div className="px-2">
        <label className="filter-label">
          <small>Associate Company</small>
        </label>
        <Select
          placeholder="Associate Company"
          options={associateCompanies}
          onChange={handleAssociateCompanyChange}
          value={associateCompany}
          className="select-control"
          components={{ DropdownIndicator }}
          isDisabled={!company}
        />
      </div>

      {(showStateCityInsteadOfLocation || showStateCity) && (
        <>
          <div className="px-2">
            <label className="filter-label">
              <small>State</small>
            </label>
            <Select
              placeholder="State"
              options={states}
              onChange={setState}
              value={state}
              className="select-control"
              components={{ DropdownIndicator }}
              isDisabled={!associateCompany || associateCompany.value === "all"}
            />
          </div>

          <div className="px-2">
            <label className="filter-label">
              <small>City</small>
            </label>
            <Select
              placeholder="City"
              options={cities}
              onChange={setCity}
              value={city}
              className="select-control"
              components={{ DropdownIndicator }}
              isDisabled={!state || state.value === "all"}
            />
          </div>
        </>
      )}

      {!showStateCityInsteadOfLocation && (
        <div className="px-2">
          <label className="filter-label">
            <small>Location</small>
          </label>
          <Select
            placeholder="Location"
            options={locations}
            onChange={setLocation}
            value={location}
            className="select-control"
            components={{ DropdownIndicator }}
            isDisabled={!associateCompany || associateCompany.value === "all"}
          />
        </div>
      )}
    </>
  );
}

export default LicenseFilters;
